import { AiIconSmall } from '@product-framework/components/ArtificalIntelligence/AiIcon';
import permissionConfig from './permissionConfig';

const menu = ({
  navigate,
  userAccess,
  store,
  IconLibrary,
  moduleSettings,
  formatMessage,
}) => {
  const menuItems = [
    {
      name: formatMessage({ id: 'MAIN_MENU_JOBS', defaultMessage: 'Jobs' }),
      id: 'JOBS',
      hasPermission: true,
      Icon: IconLibrary.ServiceJobs,
      children: [
        {
          name: formatMessage({
            id: 'MAIN_MENU_CREATE_JOB',
            defaultMessage: 'Create Job',
          }),
          hasPermission: permissionConfig({
            userAccess,
            permissionName: 'CREATE_JOB',
          }),
          tags: ['create', 'new', 'service', 'job', 'pre', 'register'],
          onClick: () => {
            navigate('/servicejobs/preregister');
          },
        },
        {
          name: formatMessage({
            id: 'MAIN_MENU_LIST_ALL_JOBS',
            defaultMessage: 'List All Jobs',
          }),
          hasPermission: true,
          tags: ['list', 'all', 'service', 'job'],
          onClick: () => {
            store.domain.list.reset('ServiceJobList');
            navigate('/servicejobs');
          },
        },
        {
          name: formatMessage({
            id: 'MAIN_MENU_SEARCH_JOBS',
            defaultMessage: 'Search Jobs',
          }),
          hasPermission: true,
          tags: ['list', 'all', 'service', 'job', 'search'],
          onClick: () => {
            navigate('/servicejobs?view=search');
          },
        },
      ],
    },
    {
      name: formatMessage({ id: 'MAIN_MENU_CLAIMS', defaultMessage: 'Claims' }),
      id: 'CLAIMS',
      hasPermission: moduleSettings.claimsPurchased
        ? userAccess.isSASupervisor() || userAccess.isAdmin()
        : false,
      Icon: IconLibrary.Claims,
      children: [
        {
          name: formatMessage({
            id: 'MAIN_MENU_CLAIM_PAYMENT_EXPORTS',
            defaultMessage: 'Claim Payment Exports',
          }),
          hasPermission: userAccess.isSASupervisor() || userAccess.isAdmin(),
          tags: ['list', 'all', 'claim', 'payment', 'export'],
          onClick: () => {
            navigate('/claim-payment-exports');
          },
        },
      ],
    },
    {
      name: formatMessage({
        id: 'MAIN_MENU_MANAGEMENT',
        defaultMessage: 'Management',
      }),
      id: 'MANAGEMENT',
      hasPermission: true,
      Icon: IconLibrary.ServiceProviders,
      children: [
        {
          name: formatMessage({
            id: 'MAIN_MENU_SERVICE_PROVIDERS',
            defaultMessage: 'Service Providers',
          }),
          hasPermission: userAccess.isSA() || userAccess.isAdmin(),
          tags: [
            'list',
            'all',
            'serviceprovider',
            'serviceproviders',
            'manage',
          ],
          onClick: () => {
            navigate('/serviceproviders');
          },
        },
        {
          name: formatMessage({
            id: 'MAIN_MENU_USERS',
            defaultMessage: 'Users',
          }),
          hasPermission: true,
          tags: ['user', 'role', 'account'],
          onClick: () => {
            if (userAccess.isSA() || userAccess.isAdmin()) {
              navigate('/users');
            } else {
              const serviceProviderId = userAccess.company.id;
              // TODO: try removing hack, importing from config, causes dependency cycle
              const usersTabNumber = 2;
              navigate(
                `/serviceproviders/edit/${serviceProviderId}/${usersTabNumber}`,
              );
            }
          },
        },
        {
          name: formatMessage({
            id: 'MAIN_MENU_SERVICE_DIRECTORY',
            defaultMessage: 'Service Directory',
          }),
          hasPermission:
            moduleSettings?.serviceDirectoryPurchased &&
            (userAccess.isSASupervisor() || userAccess.isAdmin()),
          tags: ['Documentation'],
          onClick: () => {
            navigate('/service-directory');
          },
        },
        {
          name: formatMessage({
            id: 'MAIN_MENU_INVOICES',
            defaultMessage: 'Invoices',
          }),
          hasPermission: userAccess.isSASupervisor() || userAccess.isAdmin(),
          tags: ['list', 'all', 'invoice'],
          onClick: () => {
            navigate('/invoices');
          },
        },
      ],
    },
    {
      name: formatMessage({
        id: 'MAIN_MENU_SUPPORT',
        defaultMessage: 'Support',
      }),
      id: 'SUPPORT',
      hasPermission: true,
      Icon: IconLibrary.Support,
      children: [
        {
          name: formatMessage({
            id: 'MAIN_MENU_API_DOCUMENTATION',
            defaultMessage: 'API Documentation',
          }),
          hasPermission: true,
          tags: ['Documentation'],
          onClick: () => {
            navigate('/docs');
          },
        },
        {
          name: formatMessage({
            id: 'MAIN_MENU_HELP_AND_TRAINING',
            defaultMessage: 'Help & Training',
          }),
          hasPermission: true,
          tags: ['Help'],
          onClick: () => {
            navigate('/help');
          },
        },
        {
          name: formatMessage({
            id: 'MAIN_MENU_REQUESTS_AND_FEEDBACK',
            defaultMessage: 'Requests & Feedback',
          }),
          hasPermission: true,
          tags: ['request', 'feedback', 'bug', 'support', 'triage'],
          onClick: () => {
            navigate('/issue-tracking');
          },
        },
        {
          name: formatMessage({
            id: 'MAIN_MENU_DATA_EXPORT_HISTORY',
            defaultMessage: 'Export History',
          }),
          hasPermission: true,
          tags: ['list', 'all', 'service', 'job', 'search', 'export'],
          onClick: () => {
            navigate('/data-export-history');
          },
        },
      ],
    },
  ];

  return menuItems;
};

const setup = ({
  navigate,
  userAccess,
  IconLibrary,
  moduleSettings,
  formatMessage,
}) => [
  {
    name: 'Setup',
    hasPermission: userAccess.isSA() || userAccess.isAdmin(),
    Icon: IconLibrary.Companies,
    children: [
      {
        name: 'Industries',
        hasPermission: true,
        tags: ['list', 'all', 'industry', 'manage'],
        onClick: () => {
          navigate('/industries');
        },
      },
      {
        name: 'Clients',
        hasPermission: true,
        tags: [
          'list',
          'all',
          'serviceadministratorclient',
          'serviceadministratorclients',
          'manage',
          'client',
          'administrator',
        ],
        onClick: () => {
          navigate('/serviceadministratorclients');
        },
      },
      {
        name: 'Product Lines',
        hasPermission: true,
        tags: [
          'list',
          'all',
          'serviceadministratorproductline',
          'serviceadministratorproductlines',
          'manage',
        ],
        onClick: () => {
          navigate('/serviceadministratorproductlines');
        },
      },
      {
        name: 'Repair Severities',
        hasPermission: true,
        tags: [
          'list',
          'all',
          'serviceadministratorrepairseverity',
          'serviceadministratorrepairseverities',
          'manage',
        ],
        onClick: () => {
          navigate('/serviceadministratorrepaircategories');
        },
      },
      {
        name: 'Service Types',
        hasPermission: true,
        tags: [
          'list',
          'all',
          'serviceadministratorservicetype',
          'serviceadministratorservicetypes',
          'manage',
        ],
        onClick: () => {
          navigate('/serviceadministratorservicetypes');
        },
      },
      {
        name: 'Warranty Types',
        hasPermission: true,
        tags: [
          'list',
          'all',
          'serviceadministratorwarrantytype',
          'serviceadministratorwarrantytypes',
          'manage',
        ],
        onClick: () => {
          navigate('/serviceadministratorwarrantytypes');
        },
      },
      {
        name: 'Service Sites',
        hasPermission: true,
        tags: ['list', 'all', 'servicesite', 'servicesites', 'manage'],
        onClick: () => {
          navigate('/servicesites');
        },
      },
      {
        name: 'Labor Matrix',
        hasPermission: moduleSettings.claimsPurchased,
        tags: ['list', 'all', 'labor', 'labor matrix', 'manage'],
        onClick: () => {
          navigate('/labormatrices');
        },
      },
      {
        name: 'Brands',
        hasPermission: true,
        tags: ['list', 'all', 'brand', 'manage'],
        onClick: () => {
          navigate('/brands');
        },
      },
    ],
  },
  {
    name: 'System Settings',
    hasPermission: userAccess.isSA() || userAccess.isAdmin(),
    Icon: IconLibrary.System,
    children: [
      {
        name: 'Countries',
        hasPermission: userAccess.isAdmin(),
        tags: ['list', 'all', 'country', 'countries', 'manage'],
        onClick: () => {
          navigate('/countries');
        },
      },
      {
        name: 'Job Forms',
        hasPermission: true,
        tags: ['list', 'all', 'jobform', 'jobforms', 'manage'],
        onClick: () => {
          navigate('/servicejobforms');
        },
      },
      {
        name: 'Rules',
        hasPermission: true,
        tags: ['list', 'all', 'rule', 'rules', 'manage'],
        onClick: () => {
          navigate('/rules');
        },
      },
      {
        name: 'Workflows',
        hasPermission: true,
        tags: ['list', 'all', 'workflow', 'workflows', 'manage'],
        onClick: () => {
          navigate('/workflows');
        },
      },
    ],
  },
  {
    name: 'Insights',
    hasPermission: moduleSettings.insightPurchased
      ? userAccess.isSA() || userAccess.isAdmin()
      : false,
    Icon: AiIconSmall,
    children: [
      {
        name: 'Flow Definitions',
        hasPermission: true,
        tags: ['list', 'all', 'flow', 'definitions', 'manage'],
        onClick: () => {
          navigate('/flow-definitions');
        },
      },
      {
        name: 'Insight Config',
        hasPermission: true,
        tags: ['list', 'all', 'insight', 'config', 'manage'],
        onClick: () => {
          navigate('/insight-config');
        },
      },
      {
        name: 'Managed Numbers',
        hasPermission: true,
        tags: ['list', 'all', 'managed', 'number', 'phone', 'manage'],
        onClick: () => {
          navigate('/managed-numbers');
        },
      },
    ],
  },
  {
    name: formatMessage({
      id: 'MAIN_MENU_REQUEST_AND_FEEDBACK',
      defaultMessage: 'Requests & Feedback',
    }),
    hasPermission:
      userAccess.isAdminImplementationManager() || userAccess.isSASupervisor(),
    Icon: IconLibrary.RequestsAndFeedback,
    children: [
      {
        name: formatMessage({
          id: 'MAIN_MENU_REQUEST_AND_FEEDBACK_CONFIGURATION',
          defaultMessage: 'Configuration',
        }),
        hasPermission: true,
        tags: ['request', 'feedback', 'notification', 'configuration'],
        onClick: () => {
          navigate('/feedback/configuration');
        },
      },
    ],
  },
];

export default { menu, setup };
